/* Payment instructions editor — sits on Case detail page.

Lets the creditor tell the debtor how to pay them, since LienLift no longer
processes payments. All fields optional; submit writes to /api/cases/{id}
under `payment_instructions`. */
import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Bank, Envelope, DeviceMobile, Link as LinkIcon, NotePencil, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = {
    mailing_address: "",
    zelle: "",
    venmo: "",
    cashapp: "",
    ach_routing: "",
    ach_account: "",
    ach_bank_name: "",
    payment_link: "",
    notes: "",
};

export default function PaymentInstructionsEditor({ caseId, initial, caseStatus, amountOwed, onSaved }) {
    const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
    const [saving, setSaving] = useState(false);
    const [marking, setMarking] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/cases/${caseId}`, {
                payment_instructions: Object.fromEntries(
                    Object.entries(form).filter(([, v]) => (v || "").toString().trim() !== "")
                ),
            });
            toast.success("Payment instructions saved");
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const markReceived = async () => {
        const sa = window.prompt(
            `Confirm the exact amount received from the debtor (will close this case):`,
            String(amountOwed || ""),
        );
        if (sa === null) return;
        const amount = parseFloat(sa);
        if (!Number.isFinite(amount) || amount <= 0) {
            toast.error("Enter a valid amount");
            return;
        }
        setMarking(true);
        try {
            await api.patch(`/cases/${caseId}`, { status: "settled", settled_amount: amount });
            toast.success("Marked as settled");
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setMarking(false); }
    };

    const isSettled = caseStatus === "settled";

    return (
        <Card className="p-6" data-testid="payment-instructions-editor">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="overline">How the debtor pays you</p>
                    <h2 className="font-serif text-2xl mt-2">Payment instructions</h2>
                    <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                        Fill any rail(s) you accept. These appear to the debtor on the public offer page after they accept.
                        LienLift Direct never processes the payment — funds go directly to you.
                    </p>
                </div>
                <Button onClick={save} disabled={saving || isSettled} className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-10" data-testid="save-payment-instructions">
                    <FloppyDisk size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
                </Button>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap" data-testid="mark-received-row">
                <div>
                    <p className="text-sm font-medium text-slate-900">Mark payment as received</p>
                    <p className="text-xs text-slate-500 mt-1">
                        {isSettled
                            ? "This case is already settled."
                            : "When the debtor pays you, click here to close the case."}
                    </p>
                </div>
                <Button
                    onClick={markReceived}
                    disabled={marking || isSettled}
                    variant={isSettled ? "outline" : "default"}
                    className={isSettled ? "" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                    data-testid="mark-payment-received-btn"
                >
                    {isSettled ? "Already settled" : (marking ? "Saving…" : "Mark payment received")}
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                <Field icon={Envelope} label="Mailing address (paper check)" multiline value={form.mailing_address} onChange={set("mailing_address")} placeholder="Payable to: ...\n123 Main St\nCity, ST 00000" testid="pi-mailing" />
                <Field icon={DeviceMobile} label="Zelle (email or phone)" value={form.zelle} onChange={set("zelle")} placeholder="you@example.com or (555) 555-5555" testid="pi-zelle" />
                <Field icon={DeviceMobile} label="Venmo username" value={form.venmo} onChange={set("venmo")} placeholder="@your-handle" testid="pi-venmo" />
                <Field icon={DeviceMobile} label="Cash App $cashtag" value={form.cashapp} onChange={set("cashapp")} placeholder="$YourCashtag" testid="pi-cashapp" />
                <Field icon={Bank} label="Bank name (for ACH)" value={form.ach_bank_name} onChange={set("ach_bank_name")} placeholder="e.g. Chase, Wells Fargo" testid="pi-ach-bank" />
                <div className="grid grid-cols-2 gap-3">
                    <Field icon={Bank} label="ACH routing #" value={form.ach_routing} onChange={set("ach_routing")} placeholder="9-digit routing" testid="pi-ach-routing" />
                    <Field label="ACH account #" value={form.ach_account} onChange={set("ach_account")} placeholder="account number" testid="pi-ach-account" />
                </div>
                <Field icon={LinkIcon} label="Stripe / PayPal link" value={form.payment_link} onChange={set("payment_link")} placeholder="https://buy.stripe.com/..." testid="pi-link" />
                <Field icon={NotePencil} label="Additional notes" multiline value={form.notes} onChange={set("notes")} placeholder="Include any wire instructions, reference codes, deadlines, etc." testid="pi-notes" />
            </div>
        </Card>
    );
}

function Field({ icon: Icon, label, multiline, value, onChange, placeholder, testid }) {
    return (
        <div>
            <Label className="flex items-center gap-1.5 text-xs text-slate-700">
                {Icon && <Icon size={13} weight="duotone" className="text-[#0F1E3D]" />}
                {label}
            </Label>
            {multiline ? (
                <Textarea value={value} onChange={onChange} placeholder={placeholder} rows={3} className="mt-1.5 font-mono text-sm" data-testid={testid} />
            ) : (
                <Input value={value} onChange={onChange} placeholder={placeholder} className="mt-1.5 h-11 font-mono text-sm" data-testid={testid} />
            )}
        </div>
    );
}
